/**
 * Writer self-service dashboard: my books, their current statuses, and lifetime earnings.
 * Reduces "where is my book?" support emails by giving the writer everything in one view.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, BookOpen, CheckCircle2, Clock, AlertCircle, Wallet, Calendar, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  'Wag vir Proeflees': 'bg-amber-100 text-amber-800',
  'Besig met Proeflees': 'bg-blue-100 text-blue-800',
  'Wag vir Goedkeuring': 'bg-purple-100 text-purple-800',
  'Wag vir Regmaak': 'bg-orange-100 text-orange-800',
  'Gereed vir Publikasie': 'bg-emerald-100 text-emerald-800',
  'Gepubliseer': 'bg-green-100 text-green-800',
  'Herdoen Benodig': 'bg-red-100 text-red-800',
};

const WriterDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/my/dashboard`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  }
  if (!data) return <div className="p-6 text-gray-600">Geen data nie</div>;

  return (
    <div className="space-y-6" data-testid="writer-dashboard">
      <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        My Oorsig
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm"><BookOpen className="h-4 w-4" /> Totaal Boeke</div>
          <div className="text-3xl font-bold mt-1" data-testid="writer-book-count">{data.book_count}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm"><TrendingUp className="h-4 w-4" /> Lewenslange Verdienste</div>
          <div className="text-3xl font-bold mt-1 text-emerald-700">R {data.lifetime_total_payout.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm"><Wallet className="h-4 w-4" /> Reeds Betaal</div>
          <div className="text-3xl font-bold mt-1 text-blue-700">R {data.lifetime_paid.toFixed(2)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-2 text-gray-500 text-sm"><Clock className="h-4 w-4" /> Verskuldig</div>
          <div className="text-3xl font-bold mt-1 text-amber-700">R {data.lifetime_owed.toFixed(2)}</div>
        </CardContent></Card>
      </div>

      {/* My books */}
      <Card>
        <CardHeader><CardTitle>My Boeke</CardTitle></CardHeader>
        <CardContent>
          {data.books.length === 0 ? (
            <p className="text-gray-500">Jy het nog geen boeke ingedien nie.</p>
          ) : (
            <div className="space-y-3">
              {data.books.map(b => (
                <div key={b.id} className="flex items-center justify-between border rounded p-3 hover:bg-gray-50">
                  <div>
                    <div className="font-semibold">{b.boek_naam}</div>
                    <div className="text-xs text-gray-500">{b.skrywer_naam} · Ingedien {(b.created_date || '').slice(0, 10)}</div>
                  </div>
                  <Badge className={STATUS_COLORS[b.status] || 'bg-gray-200 text-gray-800'}>
                    {b.status === 'Gepubliseer' && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                    {b.status === 'Wag vir Regmaak' && <AlertCircle className="h-3 w-3 mr-1 inline" />}
                    {b.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent payouts */}
      <Card>
        <CardHeader><CardTitle><Calendar className="h-5 w-5 inline mr-2" />Laaste 3 Maande Verdienste</CardTitle></CardHeader>
        <CardContent>
          {data.last_payouts.length === 0 ? (
            <p className="text-gray-500">Geen vorige verdienste data nie. As die admin die maandstaat oplaai, sal jou aandeel hier verskyn.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b">
                <th className="text-left py-2">Maand</th>
                <th className="text-right py-2">Aankope</th>
                <th className="text-right py-2">Huur</th>
                <th className="text-right py-2">Jou Aandeel</th>
                <th className="text-right py-2">Status</th>
              </tr></thead>
              <tbody>
                {data.last_payouts.map((p, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-2">{p.month} {p.year}</td>
                    <td className="py-2 text-right">{p.purchases}</td>
                    <td className="py-2 text-right">{p.rentals}</td>
                    <td className="py-2 text-right font-semibold text-emerald-700">R {Number(p.writer_payout || 0).toFixed(2)}</td>
                    <td className="py-2 text-right">
                      {p.paid
                        ? <Badge className="bg-green-100 text-green-800">Betaal</Badge>
                        : <Badge className="bg-amber-100 text-amber-800">Verskuldig</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WriterDashboard;
